/**
 * Request timeout helpers for weak rural networks.
 *
 * A request that never answers must never freeze a sync screen. Every
 * field sync call gets an AbortSignal that fires after `ms` milliseconds.
 * Works on older Android tablets that lack AbortSignal.timeout().
 */

export function timeoutSignal(ms: number): AbortSignal {
  const anySignal = AbortSignal as unknown as { timeout?: (ms: number) => AbortSignal };
  if (typeof anySignal.timeout === 'function') return anySignal.timeout(ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/**
 * Signal that aborts after `ms` OR when `parent` aborts — so cancelling a
 * whole sync also cancels every request still running inside it.
 */
export function timeoutSignalWith(ms: number, parent?: AbortSignal): AbortSignal {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  if (parent) {
    if (parent.aborted) controller.abort();
    else parent.addEventListener('abort', () => controller.abort(), { once: true });
  }
  controller.signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
  return controller.signal;
}

/** Reject if `promise` takes longer than `ms`. The label appears in the error. */
export function withTimeout<T>(promise: PromiseLike<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`)), ms);
    Promise.resolve(promise).then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

/** Random delay so thousands of devices reconnecting together don't hit the server at once. */
export function jitterMs(maxMs: number): number {
  return Math.floor(Math.random() * maxMs);
}

export function isTimeoutError(err: unknown): boolean {
  const msg = String((err as any)?.message || err || '').toLowerCase();
  return (err as any)?.name === 'AbortError' || msg.includes('timed out') || msg.includes('abort');
}
